import React from 'react';
import { CardTW } from '../../../../tailwindComponents/CardTW/CardTW';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { Show } from '../../../shared/Show';
import { DateField, FlexRow } from '../../../../ui-toolkit';
import { usePatchAttributesMutation } from '../../../../API/order/attributes/AttributesApi';
import { UUID } from '../../../../types/shared/UUID';
import { IContractualDatesAndExpensesData } from '../../../../types/order/attributes/IContractualDatesAndExpensesData';
import { ButtonTW } from '../../../../tailwindComponents/buttonTW/ButtonTW';
import { SimpleGrid } from '../../../../tailwindComponents/grid/SimpleGrid';
import { LabelTextTW } from '../../../../tailwindComponents/formCellTW/LabelTextTW';
import { ErrorMessageTW } from '../../../../tailwindComponents/errorMessage/ErrorMessageTW';
import { checkForMutationSuccess } from '../../../../API/checkForMutationSuccess';
import { SpinnerTW } from '../../../../tailwindComponents/spinnerTW/SpinnerTW';
import { OutsideClickHandler } from '../../../shared/InteractionManagement/OutsideClickHandler';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { updateContractualDatesAndExpensesScheme } from '../../../../validation/order/OrderAttributesValidator';
import { Form, Formik } from 'formik';
import { TextFieldTW } from '../../../../tailwindComponents/textInputTW/TextFieldTW';
import { IOrderAttributesData } from '../../../../types/order/attributes/IOrderAttributesData';
import { DateTime } from 'luxon';
import { DateFormat, getDateString } from '../../../../ui-toolkit/utilities/getDateString';
import { notEmpty } from '../../../../ui-toolkit/utilities/notEmpty';

export interface IContractualDatesAndExpensesSectionProps {
  orderId: UUID;
  orderOpenDate: Date;
  attributes?: IOrderAttributesData;
  isCollapsed: boolean;
  setIsCollapsed: (newValue: boolean) => void;
  isEditMode: boolean;
  setIsEditMode: (newValue: boolean) => void;
}

const getDueDiligenceDate = (orderOpenDate: Date, days?: number) => {
  if (!days) {
    return null;
  }
  const date = new Date(orderOpenDate);
  date.setDate(date.getDate() + (days ?? 0));
  return date;
};

const hasBeenUpdated = (contractualData?: IContractualDatesAndExpensesData): boolean => {
  return (
    getDateString(DateFormat.short, contractualData?.createdAt) !==
      getDateString(DateFormat.short, contractualData?.updatedAt) ||
    getDateString(DateFormat.hugeTime, contractualData?.createdAt) !==
      getDateString(DateFormat.hugeTime, contractualData?.updatedAt)
  );
};

export const ContractualDatesAndExpensesSection: React.FC<IContractualDatesAndExpensesSectionProps> = ({
  orderId,
  orderOpenDate,
  attributes,
  isCollapsed,
  setIsCollapsed,
  isEditMode,
  setIsEditMode,
}) => {
  const [patchAttributes, { error, isLoading }] = usePatchAttributesMutation();

  const toggleEditMode = (isEditMode: boolean) => setIsEditMode(isEditMode);

  const initialValues = {
    emdHolder: attributes?.contractualDatesAndExpenses?.emdHolder,
    emdDueDate: attributes?.contractualDatesAndExpenses?.emdDueDate,
    isDueDateRequired: !!attributes?.contractualDatesAndExpenses?.emdDueDate,
    dueDiligenceDays: attributes?.contractualDatesAndExpenses?.dueDiligenceDays,
    dueDiligenceDate: getDateString(
      DateFormat.isoDate,
      getDueDiligenceDate(orderOpenDate, attributes?.contractualDatesAndExpenses?.dueDiligenceDays)
    ),
    isDueDiligenceDateRequired: !!attributes?.contractualDatesAndExpenses?.dueDiligenceDays,
  };

  const saveUpdateAttributes = async (values: IContractualDatesAndExpensesData) => {
    const emdData: IContractualDatesAndExpensesData = {
      emdDueDate: values.emdDueDate !== initialValues.emdDueDate ? values.emdDueDate : undefined,
      emdHolder: values.emdHolder !== initialValues.emdHolder ? values.emdHolder : undefined,
      dueDiligenceDays:
        values.dueDiligenceDays !== initialValues.dueDiligenceDays ? values.dueDiligenceDays : undefined,
    };
    const body = {} as IOrderAttributesData;
    if (Object.values(emdData).some((value) => notEmpty(value))) {
      body.contractualDatesAndExpenses = emdData;
    }
    const result = await patchAttributes({ orderId, body });
    if (checkForMutationSuccess(result, 'EMD Updated', 'Error Updating EMD')) {
      toggleEditMode(false);
    }
  };

  return (
    <>
      <ErrorMessageTW rtkError={error} />
      <Formik
        initialValues={initialValues}
        onSubmit={saveUpdateAttributes}
        validationSchema={updateContractualDatesAndExpensesScheme}
        validateOnChange
        enableReinitialize
      >
        {({ submitForm, resetForm, setFieldValue }) => (
          <Form>
            <OutsideClickHandler
              enabled={isEditMode}
              onCloseAndSave={() => submitForm()}
              onClose={() => toggleEditMode(false)}
            >
              <div className={'mb-4'}>
                <CardTW
                  title={
                    <div className={'flex flex-row items-center'}>
                      <Show when={isEditMode}>
                        <ChevronDownIcon className="h-7 w-7 flex-none ease-in-out" />
                      </Show>
                      Contractual Dates And Expenses
                    </div>
                  }
                  titleActions={
                    <Show when={!isLoading} fallback={<SpinnerTW />}>
                      <Show when={!isCollapsed && !isEditMode}>
                        <ButtonTW
                          ghost
                          secondary
                          className="!-ml-6"
                          data-test-id="contractual-expenses-edit-mode-button"
                          onClick={() => toggleEditMode(true)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </ButtonTW>
                      </Show>
                      <Show when={!isCollapsed && isEditMode}>
                        <div className="flex items-center">
                          <ButtonTW type="submit" secondary>
                            Save
                          </ButtonTW>
                          <ButtonTW
                            type="button"
                            ghost
                            secondary
                            className="px-2 font-normal text-foundation-primary hover:underline"
                            onClick={() => {
                              resetForm();
                              toggleEditMode(false);
                            }}
                          >
                            Cancel
                          </ButtonTW>
                        </div>
                      </Show>
                    </Show>
                  }
                  isCollapsible={!isEditMode}
                  isCollapsed={isCollapsed}
                  onChangeCollapseState={() => setIsCollapsed(!isCollapsed)}
                  titleClassName={'text-xl'}
                  key={`emd-card-${isCollapsed}`}
                >
                  <SimpleGrid baseline>
                    <Show
                      when={isEditMode}
                      fallback={
                        <SimpleGrid columnCount={4} baseline>
                          <div>
                            <FlexRow className="font-medium">EMD Due Date</FlexRow>
                            <LabelTextTW
                              labelText={getDateString(
                                DateFormat.short,
                                attributes?.contractualDatesAndExpenses?.emdDueDate ?? ''
                              )}
                            />
                          </div>
                          <div>
                            <FlexRow className="font-medium">EMD Holder</FlexRow>
                            <LabelTextTW labelText={attributes?.contractualDatesAndExpenses?.emdHolder ?? ''} />
                          </div>
                          <div>
                            <FlexRow className="font-medium">Due Diligence Date</FlexRow>
                            <LabelTextTW
                              labelText={getDateString(
                                DateFormat.short,
                                getDueDiligenceDate(
                                  orderOpenDate,
                                  attributes?.contractualDatesAndExpenses?.dueDiligenceDays
                                )
                              )}
                            />
                          </div>
                          <div>
                            <FlexRow className="font-medium">Number of Days</FlexRow>
                            <LabelTextTW
                              labelText={attributes?.contractualDatesAndExpenses?.dueDiligenceDays?.toString() ?? ''}
                            />
                          </div>
                          <Show when={hasBeenUpdated(attributes?.contractualDatesAndExpenses)}>
                            <FlexRow className="text-sm text-gray-500">
                              Last updated on{' '}
                              {getDateString(
                                DateFormat.shortDateTimeAt,
                                `${attributes?.contractualDatesAndExpenses?.updatedAt}`
                              )}
                            </FlexRow>
                          </Show>
                        </SimpleGrid>
                      }
                    >
                      <SimpleGrid columnCount={4} baseline>
                        <DateField
                          name="emdDueDate"
                          label="EMD Due Date"
                          attachPortalInsideContainter={true}
                          isRequired={initialValues.isDueDateRequired}
                        />
                        <TextFieldTW name="emdHolder" labelText={'EMD Holder'} />
                        <DateField
                          name="dueDiligenceDate"
                          label="Due Diligence Date"
                          attachPortalInsideContainter={true}
                          isRequired={initialValues.isDueDiligenceDateRequired}
                          onChange={(value) => {
                            const date1 = DateTime.fromISO(value.value ?? '').startOf('day');
                            const formattedOpenDate = getDateString(DateFormat.isoDate, orderOpenDate);
                            const date2 = DateTime.fromISO(formattedOpenDate).startOf('day');
                            const diffDays = Math.trunc(date1.diff(date2, 'days').days);
                            if (diffDays && !Number.isNaN(diffDays)) {
                              setFieldValue('dueDiligenceDays', diffDays);
                            }
                          }}
                        />
                        <TextFieldTW
                          type={'number'}
                          name="dueDiligenceDays"
                          labelText={'Number Of Days'}
                          isRequired={initialValues.isDueDiligenceDateRequired}
                          onKeyDown={(e) => ['e', 'E', '+', '-', '.', ','].includes(e.key) && e.preventDefault()}
                          onChange={(value) => {
                            setFieldValue(
                              'dueDiligenceDate',
                              getDateString(DateFormat.isoDate, getDueDiligenceDate(orderOpenDate, Number(value)))
                            );
                          }}
                        />
                      </SimpleGrid>
                    </Show>
                  </SimpleGrid>
                </CardTW>
              </div>
            </OutsideClickHandler>
          </Form>
        )}
      </Formik>
    </>
  );
};
